import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostBinding, inject, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Utils } from "src/modules/utils/shared/utils";
import { MonacoEditorLoaderService, MonacoModule } from "../../services/monaco-editor-loader.service";

type MonacoEditor = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IStandaloneCodeEditor;
type MonacoDisposable = import("monaco-editor/esm/vs/editor/editor.api.js").IDisposable;
type MonacoEditorOptions = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IStandaloneEditorConstructionOptions;
type MonacoModel = import("monaco-editor/esm/vs/editor/editor.api.js").editor.ITextModel;
type MonacoDecorations = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IEditorDecorationsCollection;

@Component({
    selector: "app-monaco-editor",
    templateUrl: "./monaco-editor.component.html",
    styleUrl: "./monaco-editor.component.scss",
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MonacoEditorComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MonacoEditorComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

    private static logPatterns = new WeakMap<MonacoModel, RegExp>();
    private static logFoldingRegistered = false;

    private loader = inject(MonacoEditorLoaderService);
    private zone = inject(NgZone);
    private monaco: MonacoModule;
    private editor: MonacoEditor;
    private changeListener: MonacoDisposable;
    private blurListener: MonacoDisposable;
    private lineHighlight: MonacoDecorations;
    private foldTimer: ReturnType<typeof setTimeout>;
    private terminalHistory: string[] = [];
    private terminalHistoryIndex = -1;
    private pendingJump: {line: number; highlight: boolean};
    private destroyed = false;
    private disabled = false;
    private value = "";
    private onChange: (value: string) => void = () => null;
    private onTouched: () => void = () => null;

    @ViewChild("container", {static: true}) container: ElementRef<HTMLDivElement>;

    @Input()
    language = "plaintext";
    @Input()
    readOnly = false;
    @Input()
    theme: string;
    @Input()
    options: MonacoEditorOptions = {};
    @Input()
    lineBlockFoldingStartPattern: string;
    @Input()
    collapseLineBlocks = false;
    @Input()
    singleLine = false;
    @Input()
    asTerminal = false;
    @Output()
    textChange = new EventEmitter<string>();
    @Output()
    terminalCommand = new EventEmitter<string>();
    @Output()
    editorReady = new EventEmitter<void>();

    @HostBinding("class.single-line")
    get singleLineClass(): boolean {
        return this.singleLine;
    }

    async ngAfterViewInit(): Promise<void> {
        this.monaco = await this.loader.load(this.language);
        if (this.destroyed) {
            return;
        }

        this.registerLogFolding();

        this.zone.runOutsideAngular(() => {
            this.editor = this.monaco.editor.create(this.container.nativeElement, {
                automaticLayout: true,
                scrollBeyondLastLine: false,
                fontSize: 13,
                ...this.options,
                language: this.loader.normalizeLanguage(this.language),
                readOnly: this.readOnly || this.disabled,
                value: this.value
            });
            this.configureLogFolding();
            this.configureTerminal();
            if (this.theme != null) {
                this.monaco.editor.setTheme(this.theme);
            }
            this.changeListener = this.editor.onDidChangeModelContent(() => {
                let value = this.editor.getValue();
                if (this.asTerminal && /[\r\n]/.test(value)) {
                    this.editor.setValue(value.replace(/\r?\n|\r/g, ""));
                    return;
                }
                if (value != this.value) {
                    this.value = value;
                    this.zone.run(() => {
                        this.onChange(value);
                        this.textChange.emit(value);
                    });
                }
            });
            this.blurListener = this.editor.onDidBlurEditorWidget(() => this.zone.run(() => this.onTouched()));
            this.scheduleInitialFold();
            this.applyPendingJump();
        });
        this.zone.run(() => this.editorReady.emit());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.language) {
            void this.updateLanguage();
        }
        if (changes.options && this.editor != null) {
            this.editor.updateOptions(this.options ?? {});
        }
        if (changes.theme && this.editor != null && this.theme != null) {
            this.monaco.editor.setTheme(this.theme);
        }
        if ((changes.readOnly || changes.options) && this.editor != null) {
            this.updateReadOnly();
        }
        if ((changes.lineBlockFoldingStartPattern || changes.collapseLineBlocks) && this.editor != null) {
            this.configureLogFolding();
            this.scheduleInitialFold();
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.changeListener?.dispose();
        this.blurListener?.dispose();
        clearTimeout(this.foldTimer);
        this.lineHighlight?.clear();
        if (this.editor?.getModel() != null) {
            MonacoEditorComponent.logPatterns.delete(this.editor.getModel());
        }
        this.editor?.dispose();
        this.changeListener = null;
        this.blurListener = null;
        this.editor = null;
        this.monaco = null;
    }

    writeValue(value: string): void {
        this.value = value ?? "";
        if (this.editor != null && this.editor.getValue() != this.value) {
            this.editor.setValue(this.value);
            this.highlightLine(null);
            this.scheduleInitialFold();
            this.applyPendingJump();
        }
    }

    registerOnChange(onChange: (value: string) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
        this.updateReadOnly();
    }

    getValue(): string {
        return this.editor?.getValue() ?? this.value;
    }

    focus(): void {
        this.editor?.focus();
    }

    layout(): void {
        this.editor?.layout();
    }

    getEditor(): MonacoEditor {
        return this.editor;
    }

    jumpToLine(line: number, highlight = false): boolean {
        let model = this.editor?.getModel();
        if (model == null || !Number.isInteger(line) || line < 1 || line > model.getLineCount()) {
            if (Number.isInteger(line) && line > 0) {
                this.pendingJump = {line, highlight};
            }
            return false;
        }

        this.pendingJump = null;
        this.editor.layout();
        this.editor.setPosition({lineNumber: line, column: 1});
        this.editor.revealLineInCenter(line);
        if (highlight) {
            this.highlightLine(line);
        }
        return true;
    }

    private applyPendingJump(): void {
        if (this.pendingJump != null) {
            this.jumpToLine(this.pendingJump.line, this.pendingJump.highlight);
        }
    }

    highlightLine(line: number | null): void {
        this.lineHighlight?.clear();
        if (line == null || this.editor?.getModel() == null || line < 1 || line > this.editor.getModel().getLineCount()) {
            return;
        }

        this.lineHighlight ??= this.editor.createDecorationsCollection();
        this.lineHighlight.set([{
            range: new this.monaco.Range(line, 1, line, 1),
            options: {isWholeLine: true, className: "monaco-custom-highlight"}
        }]);
    }

    private registerLogFolding(): void {
        if (this.lineBlockFoldingStartPattern == null || MonacoEditorComponent.logFoldingRegistered) {
            return;
        }

        this.monaco.languages.register({id: "log-entries"});
        this.monaco.languages.registerFoldingRangeProvider("log-entries", {
            provideFoldingRanges: model => {
                let pattern = MonacoEditorComponent.logPatterns.get(model);
                if (pattern == null) {
                    return [];
                }

                let starts: number[] = [];
                for (let line = 1; line <= model.getLineCount(); line++) {
                    if (pattern.test(model.getLineContent(line))) {
                        starts.push(line);
                    }
                }
                return starts.flatMap((start, index) => {
                    let end = (starts[index + 1] ?? model.getLineCount() + 1) - 1;
                    return end > start ? [{start, end}] : [];
                });
            }
        });
        MonacoEditorComponent.logFoldingRegistered = true;
    }

    private configureLogFolding(): void {
        let model = this.editor?.getModel();
        if (model == null) {
            return;
        }
        if (this.lineBlockFoldingStartPattern) {
            MonacoEditorComponent.logPatterns.set(model, new RegExp(this.lineBlockFoldingStartPattern));
        }
        else {
            MonacoEditorComponent.logPatterns.delete(model);
        }
    }

    private scheduleInitialFold(): void {
        clearTimeout(this.foldTimer);
        if (this.collapseLineBlocks && this.lineBlockFoldingStartPattern && this.editor != null) {
            this.foldTimer = Utils.setTimerOnce(0, () => void this.editor?.getAction("editor.foldAll")?.run());
        }
    }

    private configureTerminal(): void {
        if (!this.asTerminal) {
            return;
        }

        this.editor.addCommand(this.monaco.KeyCode.Enter, () => {
            let value = this.editor.getValue().trim();
            if (value) {
                this.terminalHistory.push(value);
                this.terminalHistoryIndex = this.terminalHistory.length;
                this.zone.run(() => this.terminalCommand.emit(value));
                this.editor.setValue("");
            }
        });
        this.editor.addCommand(this.monaco.KeyCode.UpArrow, () => {
            if (this.terminalHistory.length > 0) {
                this.terminalHistoryIndex = Math.max(0, this.terminalHistoryIndex - 1);
                this.setTerminalValue(this.terminalHistory[this.terminalHistoryIndex]);
            }
        });
        this.editor.addCommand(this.monaco.KeyCode.DownArrow, () => {
            if (this.terminalHistory.length > 0) {
                this.terminalHistoryIndex = Math.min(this.terminalHistory.length, this.terminalHistoryIndex + 1);
                this.setTerminalValue(this.terminalHistory[this.terminalHistoryIndex] ?? "");
            }
        });
    }

    private setTerminalValue(value: string): void {
        this.editor.setValue(value);
        this.editor.setPosition({lineNumber: 1, column: value.length + 1});
    }

    private async updateLanguage(): Promise<void> {
        let language = this.loader.normalizeLanguage(this.language);
        this.monaco ??= await this.loader.load(language);
        if (!this.destroyed && this.editor?.getModel() != null) {
            this.monaco.editor.setModelLanguage(this.editor.getModel(), language);
        }
    }

    private updateReadOnly(): void {
        this.editor?.updateOptions({readOnly: this.readOnly || this.disabled});
    }
}
